import React from 'react';
import { Box, Typography, Paper, Grid, Stack, Chip, alpha, useTheme, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ProspectiveResidentDTO, AdmissionStatus } from '../../types/crm.types';

interface AdmissionsPipelineProps {
    leads: ProspectiveResidentDTO[];
    onLeadClick?: (lead: ProspectiveResidentDTO) => void;
    onStatusChange?: (leadId: string, newStatus: AdmissionStatus) => void;
}

const AdmissionsPipeline: React.FC<AdmissionsPipelineProps> = ({ leads, onLeadClick, onStatusChange }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [activeLeadId, setActiveLeadId] = React.useState<string | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, leadId: string) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setActiveLeadId(leadId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveLeadId(null);
    };

    const handleMove = (status: AdmissionStatus) => {
        if (activeLeadId && onStatusChange) {
            onStatusChange(activeLeadId, status);
        }
        handleMenuClose();
    };

    const statuses = Object.values(AdmissionStatus);

    const getStatusColor = (status: AdmissionStatus) => {
        switch (status) {
            case AdmissionStatus.INQUIRY: return theme.palette.info.main;
            case AdmissionStatus.TOURING: return theme.palette.warning.main;
            case AdmissionStatus.WAITLIST: return theme.palette.primary.main;
            case AdmissionStatus.ADMITTED: return theme.palette.success.main;
            case AdmissionStatus.LOST: return theme.palette.error.main;
            default: return theme.palette.grey[500];
        }
    };

    const handleDragStart = (e: React.DragEvent, leadId: string) => {
        e.dataTransfer.setData('leadId', leadId);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent, targetStatus: AdmissionStatus) => {
        e.preventDefault();
        const leadId = e.dataTransfer.getData('leadId');
        if (leadId && onStatusChange) {
            onStatusChange(leadId, targetStatus);
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2} sx={{ height: '100%', minHeight: 600 }}>
                {statuses.map((status) => (
                    <Grid item xs={12} md={2.4} key={status}>
                        <Paper
                            elevation={0}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, status)}
                            sx={{
                                p: 2,
                                height: '100%',
                                bgcolor: alpha(getStatusColor(status), 0.03),
                                border: '1px dashed',
                                borderColor: alpha(getStatusColor(status), 0.3),
                                borderRadius: 3,
                                transition: 'background-color 0.2s ease',
                                '&:hover': { bgcolor: alpha(getStatusColor(status), 0.06) }
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight="800" sx={{ color: getStatusColor(status), textTransform: 'capitalize' }}>
                                    {status.toLowerCase()}
                                </Typography>
                                <Chip 
                                    label={leads.filter(l => l.status === status).length} 
                                    size="small" 
                                    sx={{ bgcolor: alpha(getStatusColor(status), 0.1), color: getStatusColor(status), fontWeight: '700' }} 
                                />
                            </Box>
                            
                            <Stack spacing={2}>
                                {leads.filter(l => l.status === status).map((lead) => (
                                    <Paper
                                        key={lead.id}
                                        elevation={1}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, lead.id)}
                                        onClick={onLeadClick ? () => onLeadClick(lead) : undefined}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            cursor: 'grab',
                                            position: 'relative',
                                            bgcolor: 'background.paper',
                                            transition: 'all 0.2s ease',
                                            '&:active': { cursor: 'grabbing' },
                                            '&:hover': { boxShadow: theme.shadows[3], borderColor: theme.palette.primary.main }
                                        }}
                                    >
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                            <Typography variant="subtitle2" fontWeight="700">
                                                {lead.name}
                                            </Typography>
                                            <Tooltip title="Update Phase">
                                                <IconButton 
                                                    size="small" 
                                                    sx={{ mt: -0.5, mr: -0.5 }}
                                                    onClick={(e) => handleMenuOpen(e, lead.id)}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Rep: {lead.responsibleParty}
                                        </Typography>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                                            <Typography variant="caption" sx={{ bgcolor: 'action.hover', px: 1, py: 0.2, borderRadius: 0.5 }}>
                                                {lead.source}
                                            </Typography>
                                            {lead.readinessScore !== undefined && (
                                                <Typography variant="caption" fontWeight="700" color="primary">
                                                    Score: {lead.readinessScore}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                ))}
                                {leads.filter(l => l.status === status).length === 0 && (
                                    <Typography variant="caption" color="text.disabled" textAlign="center" sx={{ py: 4 }}>
                                        Empty Stage
                                    </Typography>
                                )}
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 150 } }}
            >
                <Typography variant="caption" sx={{ px: 2, py: 1, fontWeight: '700', color: 'text.secondary' }}>Next Phase</Typography>
                {statuses.map((s) => (
                    <MenuItem key={s} onClick={() => handleMove(s)}>
                        <Typography variant="body2" fontWeight="600">{s}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default AdmissionsPipeline;
